import React, { useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';

import { PrimaryButton, SecondaryButton } from '../../components';
import {
  answerListingQuestion,
  askListingQuestion,
  fetchListingQa,
} from '../../util/api';

import css from './ListingPublicQa.module.css';

/**
 * Public listing Q&A — eBay-style questions visible on the listing page.
 */
const ListingPublicQa = props => {
  const {
    listingId,
    isOwnListing,
    isAuthenticated,
    className,
    disabled,
  } = props;

  const [items, setItems] = useState([]);
  const [questionText, setQuestionText] = useState('');
  const [answerDrafts, setAnswerDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitError, setSubmitError] = useState(null);
  const [askInProgress, setAskInProgress] = useState(false);
  const [answerInProgressId, setAnswerInProgressId] = useState(null);

  const refresh = useCallback(() => {
    if (!listingId) {
      return Promise.resolve();
    }
    setLoading(true);
    return fetchListingQa(listingId)
      .then(response => setItems(response.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [listingId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleAsk = async e => {
    e.preventDefault();
    if (!questionText.trim() || askInProgress || disabled) {
      return;
    }
    setAskInProgress(true);
    setSubmitError(null);
    try {
      await askListingQuestion(listingId, questionText.trim());
      setQuestionText('');
      await refresh();
    } catch (err) {
      setSubmitError(err.message || 'Failed to submit question');
    } finally {
      setAskInProgress(false);
    }
  };

  const handleAnswer = async questionId => {
    const answerText = answerDrafts[questionId];
    if (!answerText?.trim() || answerInProgressId) {
      return;
    }
    setAnswerInProgressId(questionId);
    setSubmitError(null);
    try {
      await answerListingQuestion(listingId, questionId, answerText.trim());
      setAnswerDrafts(prev => ({ ...prev, [questionId]: '' }));
      await refresh();
    } catch (err) {
      setSubmitError(err.message || 'Failed to submit answer');
    } finally {
      setAnswerInProgressId(null);
    }
  };

  return (
    <section
      className={classNames(css.root, className)}
      aria-label="Public questions and answers"
      data-testid="listing-public-qa"
    >
      <h2 className={css.heading}>Public Q&amp;A</h2>
      <p className={css.subheading}>
        Ask the seller a question — answers are visible to everyone browsing this listing.
      </p>

      {loading ? <p className={css.loading}>Loading questions…</p> : null}

      {!loading && items.length === 0 ? (
        <p className={css.empty}>No public questions yet. Be the first to ask.</p>
      ) : null}

      <ul className={css.list}>
        {items.map(item => (
          <li key={item.id} className={css.item}>
            <p className={css.questionLabel}>Q</p>
            <p className={css.questionText}>{item.questionText}</p>
            {item.answerText ? (
              <>
                <p className={css.answerLabel}>A</p>
                <p className={css.answerText}>{item.answerText}</p>
              </>
            ) : isOwnListing ? (
              <div className={css.answerForm}>
                <textarea
                  className={css.textarea}
                  rows={3}
                  placeholder="Write a public answer…"
                  value={answerDrafts[item.id] || ''}
                  onChange={e =>
                    setAnswerDrafts(prev => ({ ...prev, [item.id]: e.target.value }))
                  }
                  disabled={disabled}
                />
                <SecondaryButton
                  className={css.answerButton}
                  inProgress={answerInProgressId === item.id}
                  disabled={disabled || answerInProgressId === item.id}
                  onClick={() => handleAnswer(item.id)}
                >
                  Post answer
                </SecondaryButton>
              </div>
            ) : (
              <p className={css.pending}>Awaiting seller response</p>
            )}
          </li>
        ))}
      </ul>

      {isAuthenticated && !isOwnListing && !disabled ? (
        <form className={css.askForm} onSubmit={handleAsk}>
          <label className={css.askLabel} htmlFor={`listing-qa-ask-${listingId}`}>
            Ask a public question
          </label>
          <textarea
            id={`listing-qa-ask-${listingId}`}
            className={css.textarea}
            rows={3}
            value={questionText}
            onChange={e => setQuestionText(e.target.value)}
            placeholder="e.g. Is this still available for pickup today?"
          />
          <PrimaryButton type="submit" inProgress={askInProgress} disabled={askInProgress}>
            Submit question
          </PrimaryButton>
        </form>
      ) : null}

      {!isAuthenticated && !disabled ? (
        <p className={css.signInHint}>Sign in to ask a public question.</p>
      ) : null}

      {submitError ? <p className={css.error}>{submitError}</p> : null}
    </section>
  );
};

export default ListingPublicQa;
